import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import { updateSocietyConfiguration } from 'modules/society/redux/societyActions';
import { useGetSocietySectionPermissions } from 'modules/society/hooks/useGetSocietySectionPermissions';
import { EquityMarketMailingListEntry } from 'types/Society';

const MAX_RECIPIENTS = 10;

const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

const UNSAFE_CHARS_REGEX = /[<>'"]/;

type Props = {
  societyId: string;
};

const IncentivesMarketAdminPanelConfigurationMailingList: FC<Props> = ({
  societyId,
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const { isReadOnly } = useGetSocietySectionPermissions().data;

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const [mailingList, setMailingList] = useState<EquityMarketMailingListEntry[]>(
    []
  );
  const [nameInput, setNameInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const savedList =
      actualSociety?.configuration?.incentivesMarket?.mailingList ?? [];
    setMailingList(savedList);
  }, [actualSociety]);

  const saveList = async (list: EquityMarketMailingListEntry[]) => {
    setIsSaving(true);
    await dispatch(
      updateSocietyConfiguration(
        societyId,
        { 'incentivesMarket.mailingList': list },
        true
      )
    );
    setIsSaving(false);
  };

  const handleAddEntry = async () => {
    const trimmedName = nameInput.trim();
    const trimmedEmail = emailInput.trim().toLowerCase();

    if (!trimmedName || UNSAFE_CHARS_REGEX.test(trimmedName)) {
      setFormError(t('IncentivesMarketMailingListInvalidName'));
      return;
    }

    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setFormError(t('IncentivesMarketMailingListInvalidEmail'));
      return;
    }

    if (mailingList.some((entry) => entry.email === trimmedEmail)) {
      setFormError(t('IncentivesMarketMailingListDuplicateEmail'));
      return;
    }

    setFormError(null);
    const newList = [...mailingList, { name: trimmedName, email: trimmedEmail }];
    setMailingList(newList);
    setNameInput('');
    setEmailInput('');
    await saveList(newList);
  };

  const handleRemoveEntry = async (index: number) => {
    const newList = mailingList.filter((_, i) => i !== index);
    setMailingList(newList);
    await saveList(newList);
  };

  const isMaxReached = mailingList.length >= MAX_RECIPIENTS;
  const canAdd =
    !isReadOnly && !isMaxReached && nameInput.trim() && emailInput.trim();

  return (
    <div className="card card-bordered mb-4">
      <div className="card-inner">
        <h6 className="card-title mb-1">
          <em className="icon ni ni-mail mr-2 text-primary" />
          {t('IncentivesMarketAdminConfigSection4Title')}
          <span className="text-soft ml-1 fs-14px font-weight-normal">
            ({mailingList.length}/{MAX_RECIPIENTS})
          </span>
        </h6>
        <p className="text-soft fs-13px mb-4">
          {t('IncentivesMarketAdminConfigSection4Description')}
        </p>

        {!isReadOnly && (
          <div className="mb-3">
            <div className="d-flex flex-wrap align-items-start" style={{ gap: '8px' }}>
              <div
                className="form-control-wrap"
                style={{ minWidth: '200px' }}
              >
                <div className="form-icon form-icon-left">
                  <em className="icon ni ni-user" />
                </div>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('IncentivesMarketMailingListNamePlaceholder')}
                  value={nameInput}
                  onChange={(e) => {
                    setNameInput(e.target.value);
                    setFormError(null);
                  }}
                  maxLength={100}
                  disabled={isMaxReached || isSaving}
                  data-testid="mailing-list-name-input"
                />
              </div>

              <div
                className="form-control-wrap"
                style={{ minWidth: '240px' }}
              >
                <div className="form-icon form-icon-left">
                  <em className="icon ni ni-mail" />
                </div>
                <input
                  type="email"
                  className="form-control"
                  placeholder={t('IncentivesMarketMailingListEmailPlaceholder')}
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value);
                    setFormError(null);
                  }}
                  disabled={isMaxReached || isSaving}
                  data-testid="mailing-list-email-input"
                />
              </div>

              <button
                type="button"
                className="btn btn-primary"
                onClick={() => handleAddEntry().catch(() => undefined)}
                disabled={!canAdd || isSaving}
                data-testid="mailing-list-add-button"
              >
                {isSaving ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  <>
                    <em className="icon ni ni-plus" />
                    <span className="ml-1">
                      {t('IncentivesMarketMailingListAddRecipient')}
                    </span>
                  </>
                )}
              </button>
            </div>

            {formError && (
              <p className="text-danger fs-12px mt-1">{formError}</p>
            )}

            {isMaxReached && (
              <p className="text-warning fs-12px mt-1">
                {t('IncentivesMarketMailingListMaxReached')}
              </p>
            )}
          </div>
        )}

        {mailingList.length === 0 ? (
          <div className="alert alert-info fs-13px py-2 mb-0">
            <em className="icon ni ni-info mr-1" />
            {t('IncentivesMarketMailingListEmptyState')}
          </div>
        ) : (
          <div className="table-responsive card card-bordered mb-0">
            <table className="table table-tranx table-hover custom-table mb-0">
              <thead>
                <tr className="tb-tnx-head">
                  <th className="tb-tnx-id">{t('Name')}</th>
                  <th className="tb-tnx-id">{t('Email')}</th>
                  {!isReadOnly && (
                    <th className="tb-tnx-id text-right" style={{ width: '60px' }} />
                  )}
                </tr>
              </thead>
              <tbody>
                {mailingList.map((entry, index) => (
                  <tr
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${entry.email}-${index}`}
                    className="tb-tnx-item"
                  >
                    <td className="tb-tnx-id align-middle">
                      <span className="fw-medium">{entry.name}</span>
                    </td>
                    <td className="tb-tnx-id align-middle">
                      <span className="text-soft">{entry.email}</span>
                    </td>
                    {!isReadOnly && (
                      <td className="tb-tnx-id align-middle text-right">
                        <button
                          type="button"
                          className="btn btn-icon btn-sm btn-trigger"
                          onClick={() =>
                            handleRemoveEntry(index).catch(() => undefined)
                          }
                          disabled={isSaving}
                          data-testid={`remove-recipient-${index}`}
                        >
                          <em className="icon ni ni-trash text-danger" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncentivesMarketAdminPanelConfigurationMailingList;
